/**
 * File: jobPollingService.ts
 * Description: Helpers to start a long-running background job and poll for
 *   its result. Heavy agent operations (capability mapping, ADR generation)
 *   run as background jobs so the request returns immediately, avoiding the
 *   Azure App Service ~230s request timeout that severs synchronous calls.
 */

import {
  AsyncJobStartResponse,
  AsyncJobStatusResponse,
} from '../types/asyncJob';

import {
  remove as removeTrackedJob,
  upsert as upsertTrackedJob,
} from './agentJobsStore';
import { apiClient, ApiError } from './apiClient';

/** Default delay between status polls, in milliseconds. */
export const DEFAULT_POLL_INTERVAL_MS = 3000;

/** Default maximum number of poll attempts (~10 minutes at 3s each). */
export const DEFAULT_MAX_POLL_ATTEMPTS = 200;

/**
 * Default number of consecutive failed status requests tolerated before the
 * poll gives up. A single slow or dropped status request (e.g. a request
 * timeout while the backend is busy running the agent) must not abort the
 * whole job, because the background work keeps running on the server.
 */
export const DEFAULT_MAX_CONSECUTIVE_POLL_FAILURES = 5;

const JOB_STATUS_SUCCEEDED = 'succeeded';
const JOB_STATUS_FAILED = 'failed';

export interface PollJobOptions {
  intervalMs?: number;
  maxAttempts?: number;
  maxConsecutiveFailures?: number;
  signal?: AbortSignal;
}

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Poll a background job until it reaches a terminal state.
 *
 * Transient failures of individual status requests (a request timeout or a
 * network blip) are retried up to {@link PollJobOptions.maxConsecutiveFailures}
 * consecutive times, since the background job keeps running on the server.
 *
 * @param projectId - The owning project id.
 * @param jobId - The background job id to poll.
 * @param options - Polling interval, attempt cap, transient-failure tolerance,
 *   and optional abort signal.
 * @returns The job result payload on success.
 * @throws ApiError when the job fails, times out, or is aborted.
 */
export async function pollJob<TResult>(
  projectId: number,
  jobId: string,
  options?: PollJobOptions,
): Promise<TResult> {
  const intervalMs = options?.intervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const maxAttempts = options?.maxAttempts ?? DEFAULT_MAX_POLL_ATTEMPTS;
  const maxConsecutiveFailures =
    options?.maxConsecutiveFailures ?? DEFAULT_MAX_CONSECUTIVE_POLL_FAILURES;

  let consecutiveFailures = 0;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (options?.signal?.aborted) {
      throw new ApiError('Job polling was cancelled');
    }

    let status: AsyncJobStatusResponse<TResult>;
    try {
      status = await apiClient.get<AsyncJobStatusResponse<TResult>>(
        `/api/project/${projectId}/jobs/${jobId}`,
      );
    } catch (error) {
      // A single failed status request (request timeout or transient network
      // error) must not abort the job: the background work keeps running on
      // the server. Tolerate a bounded number of consecutive failures, then
      // surface the last error so genuine outages still fail the poll.
      consecutiveFailures += 1;
      if (consecutiveFailures > maxConsecutiveFailures) {
        throw error;
      }
      await delay(intervalMs);
      continue;
    }

    consecutiveFailures = 0;

    if (status.status === JOB_STATUS_SUCCEEDED) {
      if (status.result === undefined || status.result === null) {
        throw new ApiError('Job completed without a result');
      }
      return status.result;
    }

    if (status.status === JOB_STATUS_FAILED) {
      throw new ApiError(status.error ?? 'Job failed');
    }

    await delay(intervalMs);
  }

  throw new ApiError('Job timed out while waiting for completion');
}

/**
 * Start a background job and poll until it completes.
 *
 * @param projectId - The owning project id.
 * @param start - Callback that issues the start request (returns the job id).
 * @param options - Polling options forwarded to {@link pollJob}.
 * @returns The job result payload on success.
 */
export async function startAndPollJob<TResult>(
  projectId: number,
  start: () => Promise<AsyncJobStartResponse>,
  options?: PollJobOptions,
): Promise<TResult> {
  const job = await start();

  // Register the job with the navigation-resilient tracker. If the page is
  // torn down by a full-reload navigation (e.g. the stage navigation bar uses
  // window.location.href), the tracker resumes polling on the next app load.
  // The in-session poll below still owns this attempt and returns its result.
  upsertTrackedJob({
    jobId: job.job_id,
    projectId,
    jobType: job.job_type,
    label: 'Background agent task',
    startedAt: Date.now(),
    status: 'running',
  });

  try {
    return await pollJob<TResult>(projectId, job.job_id, options);
  } finally {
    // This attempt finished (resolved or threw); drop the tracker entry so it
    // is not resumed again. A full reload mid-run skips this finally and leaves
    // the entry for the tracker to resume on the next load.
    removeTrackedJob(job.job_id);
  }
}
